/**
 * Mock Payment Data
 * Contains sample payments, invoices, and transactions
 */

import type { Payment, Invoice, PaymentEntity } from '../types/payment';
import {
  PaymentStatus,
  PaymentMethod,
  PaymentPurpose,
  InvoiceStatus,
} from '../types/payment';
import type { CurrencyCode } from '../utils/formatting';

/**
 * Payment Entities
 */
const nasiLemakEntity: PaymentEntity = {
  id: 'org-001',
  name: 'Nasi Lemak Food Industries Sdn Bhd',
  type: 'ORGANIZATION',
  email: 'ahmad@nasilemakfood.com.my',
  phone: '+60123456789',
  address: 'Lot 123, Jalan Perindustrian 5, Shah Alam, Selangor 40000',
};

const maisEntity: PaymentEntity = {
  id: 'org-003',
  name: 'Majlis Agama Islam Selangor (MAIS)',
  type: 'ORGANIZATION',
  email: 'halal@mais.gov.my',
  phone: '+60355148000',
  address: 'Tingkat 10, Wisma MAIS, Shah Alam, Selangor 40000',
};

const consultancyEntity: PaymentEntity = {
  id: 'org-002',
  name: 'Halal Excellence Consultancy',
  type: 'ORGANIZATION',
  email: 'siti@halalexcellence.com.my',
  phone: '+60198765432',
  address: 'Suite 15-3, Menara IGB, Kuala Lumpur 50450',
};

/**
 * Mock Payments
 */
export const mockPayments: Payment[] = [
  // Application Fee Payment (Completed)
  {
    id: 'pay-001',
    transactionId: 'TXN-2025-001234',
    amount: 1500.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.COMPLETED,
    payer: nasiLemakEntity,
    payee: maisEntity,
    purpose: PaymentPurpose.APPLICATION_FEE,
    description: 'Halal certification application fee for MAIS-2025-0123',
    referenceId: 'app-001',
    paymentMethod: PaymentMethod.ONLINE_BANKING,
    paymentDate: new Date('2025-12-15T10:30:00'),
    dueDate: new Date('2025-12-20'),
    gatewayProvider: 'iPay88',
    gatewayTransactionId: 'IPY-20251215-001234',
    receiptNumber: 'RCPT-001234',
    receiptUrl: '/mock/receipts/rcpt-001234.pdf',
    receiptIssuedAt: new Date('2025-12-15T10:35:00'),
    createdAt: new Date('2025-12-15T10:00:00'),
    updatedAt: new Date('2025-12-15T10:35:00'),
    createdBy: 'user-001',
    processedBy: 'user-007',
    processedAt: new Date('2025-12-15T10:30:00'),
  },

  // Consultation Fee Payment (Completed)
  {
    id: 'pay-002',
    transactionId: 'TXN-2025-001567',
    amount: 3500.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.COMPLETED,
    payer: nasiLemakEntity,
    payee: consultancyEntity,
    purpose: PaymentPurpose.CONSULTATION_FEE,
    description: 'Halal readiness assessment and consultation services',
    referenceId: 'app-001',
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    paymentDate: new Date('2025-12-20T14:15:00'),
    dueDate: new Date('2025-12-25'),
    receiptNumber: 'RCPT-001567',
    receiptUrl: '/mock/receipts/rcpt-001567.pdf',
    receiptIssuedAt: new Date('2025-12-20T15:00:00'),
    createdAt: new Date('2025-12-18T09:00:00'),
    updatedAt: new Date('2025-12-20T15:00:00'),
    createdBy: 'user-001',
    processedBy: 'user-004',
    processedAt: new Date('2025-12-20T14:15:00'),
  },

  // Training Fee Payment (Completed)
  {
    id: 'pay-003',
    transactionId: 'TXN-2025-001890',
    amount: 350.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.COMPLETED,
    payer: {
      id: 'user-002',
      name: 'Nurul Aisyah',
      type: 'USER',
      email: 'nurul@nasilemakfood.com.my',
      phone: '+60123456788',
    },
    payee: consultancyEntity,
    purpose: PaymentPurpose.TRAINING_FEE,
    description: 'Halal Awareness Training - Session 001',
    referenceId: 'session-001',
    paymentMethod: PaymentMethod.CREDIT_CARD,
    paymentDate: new Date('2025-12-28T16:45:00'),
    dueDate: new Date('2026-01-15'),
    gatewayProvider: 'Stripe',
    gatewayTransactionId: 'STR-20251228-001890',
    receiptNumber: 'RCPT-001890',
    receiptUrl: '/mock/receipts/rcpt-001890.pdf',
    receiptIssuedAt: new Date('2025-12-28T16:50:00'),
    createdAt: new Date('2025-12-28T16:30:00'),
    updatedAt: new Date('2025-12-28T16:50:00'),
    createdBy: 'user-002',
    processedBy: 'user-004',
    processedAt: new Date('2025-12-28T16:45:00'),
  },

  // Audit Fee Payment (Pending)
  {
    id: 'pay-004',
    transactionId: 'TXN-2026-000123',
    amount: 5000.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.PENDING,
    payer: nasiLemakEntity,
    payee: maisEntity,
    purpose: PaymentPurpose.AUDIT_FEE,
    description: 'Official halal certification audit fee',
    referenceId: 'audit-001',
    dueDate: new Date('2026-01-15'),
    createdAt: new Date('2025-12-29T10:00:00'),
    updatedAt: new Date('2025-12-29T10:00:00'),
    createdBy: 'user-007',
  },

  // Certification Fee Payment (Pending)
  {
    id: 'pay-005',
    transactionId: 'TXN-2026-000256',
    amount: 2500.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.PENDING,
    payer: nasiLemakEntity,
    payee: maisEntity,
    purpose: PaymentPurpose.CERTIFICATION_FEE,
    description: 'Halal certificate issuance fee (2 years)',
    referenceId: 'app-001',
    dueDate: new Date('2026-02-01'),
    createdAt: new Date('2025-12-30T09:00:00'),
    updatedAt: new Date('2025-12-30T09:00:00'),
    createdBy: 'user-007',
  },

  // Additional Training Payments
  {
    id: 'pay-006',
    transactionId: 'TXN-2025-002100',
    amount: 250.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.COMPLETED,
    payer: {
      id: 'user-001',
      name: 'Ahmad bin Abdullah',
      type: 'USER',
      email: 'ahmad@nasilemakfood.com.my',
      phone: '+60123456789',
    },
    payee: consultancyEntity,
    purpose: PaymentPurpose.TRAINING_FEE,
    description: 'Halal Awareness Training - Session 003 (Online)',
    referenceId: 'session-003',
    paymentMethod: PaymentMethod.E_WALLET,
    paymentDate: new Date('2025-11-25T11:20:00'),
    dueDate: new Date('2025-12-10'),
    gatewayProvider: 'TouchNGo',
    gatewayTransactionId: 'TNG-20251125-002100',
    receiptNumber: 'RCPT-002100',
    receiptUrl: '/mock/receipts/rcpt-002100.pdf',
    receiptIssuedAt: new Date('2025-11-25T11:25:00'),
    createdAt: new Date('2025-11-25T11:15:00'),
    updatedAt: new Date('2025-11-25T11:25:00'),
    createdBy: 'user-001',
    processedBy: 'user-004',
    processedAt: new Date('2025-11-25T11:20:00'),
  },

  {
    id: 'pay-007',
    transactionId: 'TXN-2026-000345',
    amount: 1200.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.PENDING,
    payer: {
      id: 'user-001',
      name: 'Ahmad bin Abdullah',
      type: 'USER',
      email: 'ahmad@nasilemakfood.com.my',
      phone: '+60123456789',
    },
    payee: consultancyEntity,
    purpose: PaymentPurpose.TRAINING_FEE,
    description: 'Halal Executive Training - Session 002',
    referenceId: 'session-002',
    dueDate: new Date('2026-02-05'),
    createdAt: new Date('2025-12-29T14:00:00'),
    updatedAt: new Date('2025-12-29T14:00:00'),
    createdBy: 'user-001',
  },

  // Failed Payment (Credit Card Declined)
  {
    id: 'pay-008',
    transactionId: 'TXN-2025-001999',
    amount: 1500.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.FAILED,
    payer: nasiLemakEntity,
    payee: maisEntity,
    purpose: PaymentPurpose.APPLICATION_FEE,
    description: 'Application fee payment attempt (FAILED - Card declined)',
    referenceId: 'app-001',
    paymentMethod: PaymentMethod.CREDIT_CARD,
    dueDate: new Date('2025-12-20'),
    gatewayProvider: 'Stripe',
    gatewayTransactionId: 'STR-20251214-001999',
    gatewayResponse: { error: 'Card declined', code: 'card_declined' },
    createdAt: new Date('2025-12-14T15:30:00'),
    updatedAt: new Date('2025-12-14T15:32:00'),
    createdBy: 'user-001',
  },

  // Refunded Payment
  {
    id: 'pay-009',
    transactionId: 'TXN-2025-001345',
    amount: 350.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.REFUNDED,
    payer: {
      id: 'user-003',
      name: 'Aziz Rahman',
      type: 'USER',
      email: 'aziz@halalexcellence.com.my',
      phone: '+60198765431',
    },
    payee: consultancyEntity,
    purpose: PaymentPurpose.TRAINING_FEE,
    description: 'Training fee refund - Session cancelled',
    referenceId: 'session-004',
    paymentMethod: PaymentMethod.CREDIT_CARD,
    paymentDate: new Date('2025-11-15T10:00:00'),
    dueDate: new Date('2025-11-20'),
    gatewayProvider: 'Stripe',
    gatewayTransactionId: 'STR-20251115-001345',
    receiptNumber: 'RCPT-001345-REFUND',
    receiptUrl: '/mock/receipts/rcpt-001345-refund.pdf',
    receiptIssuedAt: new Date('2025-11-22T14:00:00'),
    createdAt: new Date('2025-11-15T10:00:00'),
    updatedAt: new Date('2025-11-22T14:00:00'),
    createdBy: 'user-003',
    processedBy: 'user-004',
    processedAt: new Date('2025-11-22T14:00:00'),
  },

  // Processing Payment
  {
    id: 'pay-010',
    transactionId: 'TXN-2025-002456',
    amount: 3500.0,
    currency: 'MYR' as CurrencyCode,
    status: PaymentStatus.PROCESSING,
    payer: nasiLemakEntity,
    payee: consultancyEntity,
    purpose: PaymentPurpose.CONSULTATION_FEE,
    description: 'Pre-audit consultation fee (Processing)',
    referenceId: 'app-003',
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    dueDate: new Date('2026-01-05'),
    gatewayProvider: 'Manual Bank Transfer',
    createdAt: new Date('2025-12-30T08:00:00'),
    updatedAt: new Date('2025-12-30T08:05:00'),
    createdBy: 'user-001',
  },
];

/**
 * Mock Invoices
 */
export const mockInvoices: Invoice[] = [
  // Application Fee Invoice (Paid)
  {
    id: 'inv-001',
    invoiceNumber: 'MAIS-INV-2025-0123',
    status: InvoiceStatus.PAID,
    billFrom: maisEntity,
    billTo: nasiLemakEntity,
    issueDate: new Date('2025-12-10'),
    dueDate: new Date('2025-12-20'),
    paidDate: new Date('2025-12-15'),
    items: [
      {
        id: 'item-001',
        description: 'Halal Certification Application Processing Fee',
        quantity: 1,
        unitPrice: 1500.0,
        amount: 1500.0,
      },
    ],
    subtotal: 1500.0,
    taxAmount: 0,
    totalAmount: 1500.0,
    currency: 'MYR' as CurrencyCode,
    paymentId: 'pay-001',
    paymentStatus: PaymentStatus.COMPLETED,
    notes: 'Payment due within 10 days of invoice date',
    terms: 'Net 10 days',
    invoiceUrl: '/mock/invoices/mais-inv-2025-0123.pdf',
    createdAt: new Date('2025-12-10'),
    updatedAt: new Date('2025-12-15'),
    createdBy: 'user-007',
  },

  // Consultation Invoice (Paid)
  {
    id: 'inv-002',
    invoiceNumber: 'HEC-INV-2025-0045',
    status: InvoiceStatus.PAID,
    billFrom: consultancyEntity,
    billTo: nasiLemakEntity,
    issueDate: new Date('2025-12-18'),
    dueDate: new Date('2025-12-25'),
    paidDate: new Date('2025-12-20'),
    items: [
      {
        id: 'item-002',
        description: 'Halal Readiness Assessment',
        quantity: 1,
        unitPrice: 2000.0,
        amount: 2000.0,
      },
      {
        id: 'item-003',
        description: 'Pre-Audit Consultation (2 days)',
        quantity: 2,
        unitPrice: 750.0,
        amount: 1500.0,
      },
    ],
    subtotal: 3500.0,
    taxAmount: 0,
    totalAmount: 3500.0,
    currency: 'MYR' as CurrencyCode,
    paymentId: 'pay-002',
    paymentStatus: PaymentStatus.COMPLETED,
    notes: 'Bank transfer preferred',
    terms: 'Net 7 days',
    invoiceUrl: '/mock/invoices/hec-inv-2025-0045.pdf',
    createdAt: new Date('2025-12-18'),
    updatedAt: new Date('2025-12-20'),
    createdBy: 'user-004',
  },

  // Audit Fee Invoice (Issued, Unpaid)
  {
    id: 'inv-003',
    invoiceNumber: 'MAIS-INV-2026-0001',
    status: InvoiceStatus.SENT,
    billFrom: maisEntity,
    billTo: nasiLemakEntity,
    issueDate: new Date('2025-12-29'),
    dueDate: new Date('2026-01-15'),
    items: [
      {
        id: 'item-004',
        description: 'Official Halal Certification Audit',
        quantity: 1,
        unitPrice: 5000.0,
        amount: 5000.0,
      },
    ],
    subtotal: 5000.0,
    taxAmount: 0,
    totalAmount: 5000.0,
    currency: 'MYR' as CurrencyCode,
    paymentId: 'pay-004',
    paymentStatus: PaymentStatus.PENDING,
    notes: 'Payment required before audit commencement',
    terms: 'Net 17 days',
    invoiceUrl: '/mock/invoices/mais-inv-2026-0001.pdf',
    createdAt: new Date('2025-12-29'),
    updatedAt: new Date('2025-12-29'),
    createdBy: 'user-007',
  },

  // Certification Invoice (Issued)
  {
    id: 'inv-004',
    invoiceNumber: 'MAIS-INV-2026-0002',
    status: InvoiceStatus.ISSUED,
    billFrom: maisEntity,
    billTo: nasiLemakEntity,
    issueDate: new Date('2025-12-30'),
    dueDate: new Date('2026-02-01'),
    items: [
      {
        id: 'item-005',
        description: 'Halal Certificate Issuance Fee (2 years)',
        quantity: 1,
        unitPrice: 2500.0,
        amount: 2500.0,
      },
    ],
    subtotal: 2500.0,
    taxAmount: 0,
    totalAmount: 2500.0,
    currency: 'MYR' as CurrencyCode,
    paymentId: 'pay-005',
    paymentStatus: PaymentStatus.PENDING,
    notes: 'Certificate will be issued upon payment confirmation',
    terms: 'Net 33 days',
    invoiceUrl: '/mock/invoices/mais-inv-2026-0002.pdf',
    createdAt: new Date('2025-12-30'),
    updatedAt: new Date('2025-12-30'),
    createdBy: 'user-007',
  },

  // Training Invoice (Paid)
  {
    id: 'inv-005',
    invoiceNumber: 'HEC-INV-2025-0078',
    status: InvoiceStatus.PAID,
    billFrom: consultancyEntity,
    billTo: {
      id: 'user-002',
      name: 'Nurul Aisyah',
      type: 'USER',
      email: 'nurul@nasilemakfood.com.my',
      phone: '+60123456788',
    },
    issueDate: new Date('2025-12-28'),
    dueDate: new Date('2026-01-15'),
    paidDate: new Date('2025-12-28'),
    items: [
      {
        id: 'item-006',
        description: 'Halal Awareness Training - January 2026 Session',
        quantity: 1,
        unitPrice: 350.0,
        amount: 350.0,
      },
    ],
    subtotal: 350.0,
    taxAmount: 0,
    totalAmount: 350.0,
    currency: 'MYR' as CurrencyCode,
    paymentId: 'pay-003',
    paymentStatus: PaymentStatus.COMPLETED,
    invoiceUrl: '/mock/invoices/hec-inv-2025-0078.pdf',
    createdAt: new Date('2025-12-28'),
    updatedAt: new Date('2025-12-28'),
    createdBy: 'user-004',
  },

  // Draft Invoice
  {
    id: 'inv-006',
    invoiceNumber: 'HEC-DRAFT-2026-0001',
    status: InvoiceStatus.DRAFT,
    billFrom: consultancyEntity,
    billTo: nasiLemakEntity,
    issueDate: new Date('2026-01-05'),
    dueDate: new Date('2026-01-20'),
    items: [
      {
        id: 'item-007',
        description: 'Follow-up Consultation',
        quantity: 1,
        unitPrice: 1500.0,
        amount: 1500.0,
      },
    ],
    subtotal: 1500.0,
    taxAmount: 0,
    totalAmount: 1500.0,
    currency: 'MYR' as CurrencyCode,
    paymentStatus: PaymentStatus.PENDING,
    createdAt: new Date('2025-12-30'),
    updatedAt: new Date('2025-12-30'),
    createdBy: 'user-004',
  },
];

/**
 * Helper to get payment by ID
 */
export function getPaymentById(id: string): Payment | undefined {
  return mockPayments.find((payment) => payment.id === id);
}

/**
 * Helper to get payments by payer ID
 */
export function getPaymentsByPayerId(payerId: string): Payment[] {
  return mockPayments.filter((payment) => payment.payer.id === payerId);
}

/**
 * Helper to get payments by status
 */
export function getPaymentsByStatus(status: PaymentStatus): Payment[] {
  return mockPayments.filter((payment) => payment.status === status);
}

/**
 * Helper to get invoice by ID
 */
export function getInvoiceById(id: string): Invoice | undefined {
  return mockInvoices.find((invoice) => invoice.id === id);
}

/**
 * Helper to get invoices by bill-to ID
 */
export function getInvoicesByBillToId(billToId: string): Invoice[] {
  return mockInvoices.filter((invoice) => invoice.billTo.id === billToId);
}

/**
 * Helper to get overdue invoices
 */
export function getOverdueInvoices(): Invoice[] {
  const now = new Date();
  return mockInvoices.filter(
    (invoice) =>
      invoice.status !== InvoiceStatus.PAID &&
      invoice.status !== InvoiceStatus.CANCELLED &&
      invoice.dueDate < now
  );
}

/**
 * Helper to get payment statistics
 */
export function getPaymentStatistics() {
  const completed = mockPayments.filter(
    (p) => p.status === PaymentStatus.COMPLETED
  ).length;
  const pending = mockPayments.filter((p) => p.status === PaymentStatus.PENDING).length;
  const failed = mockPayments.filter((p) => p.status === PaymentStatus.FAILED).length;
  const totalRevenue = mockPayments
    .filter((p) => p.status === PaymentStatus.COMPLETED)
    .reduce((sum, p) => sum + p.amount, 0);

  return {
    totalTransactions: mockPayments.length,
    completedPayments: completed,
    pendingPayments: pending,
    failedPayments: failed,
    totalRevenue,
  };
}
